/**
 * Un testo scritto da un **operatore**, in una o più lingue.
 *
 * È la cosa che serviva e non c'era. Il player ha due modi di avere un testo, e nessuno dei due
 * regge qui:
 *
 *  - una **chiave i18n** che il player traduce: funziona solo per un elenco chiuso deciso da noi, e
 *    un operatore che vuole chiamare una fascia «Serie A» non ha una chiave da scegliere;
 *  - un **testo libero**: arriva identico a ogni apparecchio, quindi un cliente turco legge
 *    l'italiano.
 *
 * Qui il testo è **dato**, una lingua per volta, scritto da chi vende. Un operatore compila le
 * lingue che gli servono e non le altre.
 *
 * ## Il ripiego, che è l'opposto della regola sulle chiavi
 *
 * Per le chiavi i18n il player **non ripiega sull'inglese**: una chiave che manca si mostra com'è,
 * brutta e onesta, perché una traduzione dimenticata da noi deve vedersi
 * ([14-LINGUE-E-TESTI](../../docs/14-LINGUE-E-TESTI.md) §3).
 *
 * Qui la regola è rovesciata, e non è un'incoerenza: **una lingua vuota non è una dimenticanza, è
 * una scelta**. Un rivenditore che vende solo in Italia compila l'italiano e basta, e mostrare
 * `[label]` a un cliente con il televisore in inglese sarebbe punirlo per una cosa che nessuno ha
 * sbagliato. Quindi si ripiega — e il pannello dice **a quale lingua**, mentre si scrive.
 *
 * L'ordine del ripiego è quello di `LOCALES`, e non è arbitrario: la prima lingua che l'operatore ha
 * compilato è quella che userà, e il pannello la nomina. Nessuna preferenza nascosta per l'inglese.
 */

/** Le sette lingue, nello stesso ordine di `LOCALES` in `@player/ui`. */
export const TEXT_LOCALES = ['en', 'it', 'es', 'fr', 'de', 'pl', 'tr'] as const;

export type TextLocale = (typeof TEXT_LOCALES)[number];

/**
 * Le lingue compilate, e solo quelle.
 *
 * Le chiavi assenti e le stringhe vuote significano la stessa cosa — «questa lingua non l'ho
 * scritta» — e vanno trattate uguale: un campo svuotato nel pannello lascia una stringa vuota, non
 * toglie la chiave.
 */
export type LocalizedText = Partial<Record<TextLocale, string>>;

/**
 * Il testo da mostrare, e la lingua da cui viene.
 *
 * La seconda serve al pannello e alla diagnosi: «questa fascia si chiama Calcio perché l'italiano è
 * l'unica lingua compilata» è una frase che si può dire solo se si sa da dove arriva il testo.
 */
export interface ResolvedText {
  readonly text: string;
  readonly locale: TextLocale;
  /** `true` quando la lingua chiesta non c'era e si è ripiegato. */
  readonly fallback: boolean;
}

/** Il testo nella lingua chiesta, o il ripiego. `null` quando non c'è **nessuna** lingua. */
export function resolveLocalizedText(
  label: LocalizedText | null | undefined,
  locale: TextLocale,
): ResolvedText | null {
  if (!label) return null;

  const exact = label[locale];
  if (exact && exact.trim() !== '') {
    return { text: exact.trim(), locale, fallback: false };
  }

  const ripiego = fallbackLocaleOf(label);
  if (!ripiego) return null;

  return { text: (label[ripiego] as string).trim(), locale: ripiego, fallback: true };
}

/**
 * La lingua su cui si ripiega: la prima compilata, nell'ordine di `TEXT_LOCALES`.
 *
 * Esportata perché **il pannello la deve dire mentre si scrive**. Un operatore che compila solo
 * l'italiano deve leggere «le altre lingue mostreranno l'italiano» prima di salvare, non scoprirlo
 * da uno screenshot di un cliente.
 */
export function fallbackLocaleOf(label: LocalizedText | null | undefined): TextLocale | null {
  if (!label) return null;
  for (const locale of TEXT_LOCALES) {
    const value = label[locale];
    if (value && value.trim() !== '') return locale;
  }
  return null;
}

/** Quante lingue sono compilate. Zero significa «senza titolo», che è un caso legittimo. */
export function filledLocales(label: LocalizedText | null | undefined): ReadonlyArray<TextLocale> {
  if (!label) return [];
  return TEXT_LOCALES.filter((locale) => {
    const value = label[locale];
    return value !== undefined && value.trim() !== '';
  });
}
